<template>
  <ElForm
    ref="campaignInfoForm"
    :model="campaignInfoModel"
    :rules="campaignInfoRules"
    :disabled="isLoading"
    label-position="top"
    @submit.native.prevent="updateCampaignInfo"
  >
    <ElFormItem
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.NAME')"
      prop="name"
    >
      <ElInput v-model="campaignInfoModel.name" dir="ltr" />
    </ElFormItem>

    <FormItemWithHint
      prop="budget"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.BUDGET')"
      :hint="$t('HINTS.DISCOVERY.BUDGET')"
    >
      <ElInput v-model="campaignInfoModel.budget" name="budget" required />
    </FormItemWithHint>

    <ElFormItem
      prop="startDate"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.START_DATE')"
    >
      <UiDatePickerEndFuture
        :default-date="getDateValue(campaignInfoModel.startDate)"
        @select-date="setStartDate"
      />
    </ElFormItem>

    <FormItemWithHint
      prop="endDate"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.END_DATE')"
      :hint="$t('HINTS.DISCOVERY.END_DATE')"
    >
      <UiDatePickerEndFuture
        :default-date="getDateValue(campaignInfoModel.endDate)"
        @select-date="setEndDate"
      />
    </FormItemWithHint>

    <ElFormItem
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.STATUS')"
      prop="isPaused"
    >
      <AlternativeSelect
        key="value"
        v-model="campaignInfoModel.isPaused"
        :items="pausedOptions"
      />
    </ElFormItem>

    <ElFormItem>
      <ElCheckbox
        v-model="campaignInfoModel.hasTracker"
        @input="handleHasTrackerChange"
      >
        {{ $t('ADVERTISEMENT.CAMPAIGNS.CHOOSE_TO_HAVE_TRACKER') }}
      </ElCheckbox>
    </ElFormItem>
    <ElCollapseTransition>
      <div v-show="campaignInfoModel.hasTracker">
        <FormItemWithHint
          prop="clickTrackerLink"
          :label="
            $t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.IMPRESSION_TRACKER_LINK')
          "
          :hint="$t('HINTS.DISCOVERY.TRACKER_EXAMPLES')"
        >
          <ElInput
            v-model="campaignInfoModel.clickTrackerLink"
            type="url"
            dir="ltr"
          />
          <InputHelper type="warning" el-icon="el-icon-warning">
            {{ $t('ADVERTISEMENT.CAMPAIGNS.AVAILABLE_TRACKERS') }}
          </InputHelper>
        </FormItemWithHint>
      </div>
    </ElCollapseTransition>

    <FormItemWithHint
      prop="targetedCpi"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.TARGETED_CPI')"
      :hint="$t('HINTS.DISCOVERY.TARGET_CPI')"
    >
      <ElInput
        v-model="campaignInfoModel.targetedCpi"
        name="targetedCpi"
        required
      />
    </FormItemWithHint>

    <ElFormItem
      prop="isDiscoveryAdsEnabled"
      class="campaign-info-form__is-discovery-enabled"
    >
      <ElCheckbox v-model="campaignInfoModel.isDiscoveryAdsEnabled">
        {{ $t('ADVERTISEMENT.NEW_CAMPAIGN_BASIC.ENABLE_DISCOVERY_ADS') }}
        <Hint :message="$t('HINTS.BASIC.ENABLE_DISCOVERY_ADS')" />
      </ElCheckbox>
    </ElFormItem>

    <ElButton
      :loading="isLoading"
      type="primary"
      native-type="submit"
      size="large"
    >
      {{ $t('SAVE') }}
    </ElButton>
    <ElButton type="text" @click.native="$emit('cancel')">
      {{ $t('CANCEL') }}
    </ElButton>
  </ElForm>
</template>

<script>
import UiDatePickerEndFuture from '@@/common/UiDatePickerEndFuture.vue';
import FormItemWithHint from '@@/common/FormItemWithHint';
import InputHelper from '@@/common/InputHelper';
import { dateFormat } from '@/utils/date';
import form from '@/mixins/form';
import campaignId from '@/mixins/advertisement/campaignId';
import { BasicAdsCampaigns } from '@/api/api';
import AlternativeSelect from '@@/common/AlternativeSelect';
import required from '@/utils/validation-rules/required';
import { v4 as uuid } from 'uuid';
import Hint from '@@/common/Hint';

export default {
  name: 'CampaignInfoForm',

  components: {
    UiDatePickerEndFuture,
    FormItemWithHint,
    InputHelper,
    AlternativeSelect,
    Hint,
  },

  mixins: [form, campaignId],

  props: {
    data: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      campaignInfoModel: {
        idempotencyKey: uuid(),
        name: this.data.name,
        budget: this.data.remainingBudget,
        startDate: this.data.startDate,
        endDate: this.data.endDate,
        isPaused: this.data.isPaused,
        targetedCpi: this.data.targetedCpi,
        clickTrackerLink: this.data.clickTrackerLink,
        hasTracker: Boolean(this.data.clickTrackerLink),
        isDiscoveryAdsEnabled: this.data.isDiscoveryAdsEnabled,
      },
    };
  },

  computed: {
    campaignInfoRules() {
      const requiredRule = required(this.$t('VALIDATION_REQUIRED_ERR'));

      return {
        name: requiredRule,
        startDate: requiredRule,
        endDate: requiredRule,
        clickTrackerLink: {
          type: 'url',
          message: this.$t('VALIDATION.URL_IS_INVALID'),
          trigger: 'change',
        },
      };
    },

    pausedOptions() {
      return [
        {
          label: this.$t('ADVERTISEMENT.CAMPAIGNS.ACTIVE'),
          value: false,
        },
        {
          label: this.$t('ADVERTISEMENT.CAMPAIGNS.PAUSED'),
          value: true,
        },
      ];
    },
  },

  methods: {
    updateCampaignInfo() {
      if (!this.isValid('campaignInfoForm')) {
        return false;
      }
      this.isLoading = true;

      const updatedBudget =
        this.data.budget -
        (this.data.remainingBudget - this.campaignInfoModel.budget);

      return BasicAdsCampaigns.updateCampaign({
        campaignId: this.campaignId,
        campaignInfo: {
          ...this.campaignInfoModel,
          budget: updatedBudget,
        },
      })
        .then(message => {
          this.$message.success(
            message || this.$t('ADVERTISEMENT.BASIC.UPDATE_CAMPAIGN_SUCCESS'),
          );
          this.$emit('update');
        })
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getDateValue(date) {
      return new Date(date).valueOf();
    },

    setStartDate(selectedDate) {
      this.campaignInfoModel.startDate = dateFormat()('YYYY-MM-DDT00:00:00')(
        selectedDate,
      );
    },

    setEndDate(selectedDate) {
      this.campaignInfoModel.endDate = dateFormat()('YYYY-MM-DDT23:59:59')(
        selectedDate,
      );
    },

    handleHasTrackerChange(hasTracker) {
      if (!hasTracker) {
        this.resetTracker();
      }
    },

    resetTracker() {
      this.campaignInfoModel.clickTrackerLink = '';
    },
  },
};
</script>

<style lang="scss">
.campaign-info-form {
  &__is-discovery-enabled {
    .el-checkbox {
      display: flex;
      align-items: baseline;
    }

    .el-checkbox__label {
      white-space: normal;
    }
  }
}
</style>
